import React from "react";
import { sanitizeSvg } from "../../utils/sanitize";
import {
  CardWrapper,
  CardImageWrapper,
  CardCountdownBadge,
  CardContent,
  CardHeader,
  CardBody,
  CardFooter,
  CardTag,
  CardTitle,
  CardDescription,
  CardSubtitle,
  CardDivider,
  CardMetadata,
  CardButton,
  CardButtonVisual,
  CardSpecifications,
  CardSpecificationItem,
  CardSpecificationIcon,
  CardSpecificationText,
  CardSpecificationTitle,
  CardSpecificationContent,
} from "./DynamicCard.styles";
import { formatGtagLabel } from "../../utils/formatGtagLabel";
import { InterpolateResult } from "../../utils/interpolate";
import { afField } from '@autodromo/contextual-fields';

export interface SpecificationItem {
  id?: string | number;
  icon: string;
  title: React.ReactNode;
  content?: InterpolateResult;
}

export interface DynamicCardProps {
  fieldPath?: string | null;
  specsFieldPath?: string | null;
  type: "default" | "simple" | "overlaid" | "seminovos";
  image?: React.ReactNode;
  tag?: InterpolateResult;
  title: InterpolateResult;
  description?: InterpolateResult;
  subtitle?: InterpolateResult;
  metadata?: InterpolateResult;
  buttonText?: InterpolateResult;
  buttonLink?: string;
  openLinkInNewTab?: boolean;

  alignment: "left" | "center";
  buttonStyle: "button" | "link";

  showImage?: boolean;
  showTag?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  showSubtitle?: boolean;
  showMetadata?: boolean;
  showButton?: boolean;

  titleColor?: string;
  descriptionColor?: string;
  shadowColor?: string;

  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;

  tagBgColor?: string;
  tagBorderColor?: string;
  tagTextColor?: string;

  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonBorderRadius?: string;
  buttonLinkColor?: string;
  buttonBorderColor?: string;

  imageAspectRatio?: "fixed" | "auto";
  htmlId?: string;
  titleTag?: "h2" | "h3" | "h4" | "h5" | "h6";
  specifications?: SpecificationItem[];
  specificationIconColor?: string;

  /** Badge de cronômetro sobreposto na imagem do card (undefined = sem badge). */
  countdown?: React.ReactNode;
  /** Canto do badge sobre a imagem. Default: "left". */
  countdownPosition?: "left" | "right";
}

export const DynamicCard: React.FC<DynamicCardProps> = ({
  fieldPath,
  specsFieldPath,
  type,
  image,
  tag,
  title,
  description,
  subtitle,
  metadata,
  buttonText,
  buttonLink,
  openLinkInNewTab = false,
  alignment,
  buttonStyle,
  showImage = true,
  showTag = true,
  showTitle = true,
  showDescription = true,
  showSubtitle = true,
  showMetadata = true,
  showButton = true,
  backgroundColor,
  borderColor,
  borderRadius,
  tagBgColor,
  tagBorderColor,
  tagTextColor,
  buttonBgColor,
  buttonTextColor,
  buttonBorderRadius,
  htmlId,
  titleTag = "h3",
  titleColor,
  descriptionColor,
  shadowColor,
  buttonLinkColor,
  buttonBorderColor,
  specifications,
  specificationIconColor,
  imageAspectRatio,
  countdown,
  countdownPosition,
}) => {
  const renderImage = () => {
    if (!showImage || !image) return null;

    return (
      <CardImageWrapper
        data-af-field={afField(fieldPath, 'image', 'props', 'src')}
        as={buttonLink ? "div" : "a"}
        $type={type}
        $borderRadius={borderRadius}
        $shadowColor={shadowColor}
        $imageAspectRatio={imageAspectRatio}
        href={buttonLink || undefined}
        target={buttonLink && openLinkInNewTab ? "_blank" : "_self"}
        rel={buttonLink && openLinkInNewTab ? "noopener noreferrer" : undefined}
      >
        {image}
        {countdown && (
          <CardCountdownBadge $position={countdownPosition === "right" ? "right" : "left"}>
            {countdown}
          </CardCountdownBadge>
        )}
      </CardImageWrapper>
    );
  };

  const renderTag = () => {
    const tagNode = tag?.node;
    if (!showTag || !tagNode) return null;

    return (
      <CardTag
        data-af-field={afField(fieldPath, 'tag')}
        $backgroundColor={tagBgColor}
        $borderColor={tagBorderColor}
        $textColor={tagTextColor}
      >
        {tagNode}
      </CardTag>
    );
  };

  const renderTitle = () => {
    const titleNode = title?.node;
    if (!showTitle || !titleNode) return null;

    return (
      <CardTitle
        data-af-field={afField(fieldPath, 'title')}
        as={titleTag}
        $titleColor={titleColor}
      >
        {titleNode}
      </CardTitle>
    );
  };

  const renderDescription = () => {
    const descriptionNode = description?.node;
    if (!showDescription || !descriptionNode) return null;

    return (
      <CardDescription
        data-af-field={afField(fieldPath, 'description')}
        $descriptionColor={descriptionColor}
        $noClamp={type === "seminovos"}
        className="rich-text-reset-html"
      >
        {descriptionNode}
      </CardDescription>
    );
  };

  const renderMetadata = () => {
    const metadataNode = metadata?.node;
    if (!showMetadata || !metadataNode) return null;

    return (
      <CardMetadata $metadataColor={descriptionColor} data-af-field={afField(fieldPath, 'metadata')}>{metadataNode}</CardMetadata>
    );
  };

  const renderSpecifications = () => {
    if (type !== "seminovos" || !specifications || specifications.length === 0)
      return null;

    return (
      <CardSpecifications>
        {specifications.map((spec, index) => (
          <CardSpecificationItem key={index} data-af-field={afField(specsFieldPath, spec.id ?? index)}>
            <CardSpecificationIcon
              dangerouslySetInnerHTML={{ __html: sanitizeSvg(spec.icon) }}
              $iconColor={specificationIconColor}
            />
            <CardSpecificationText>
              <CardSpecificationTitle>{spec.title}</CardSpecificationTitle>
              <CardSpecificationContent>
                {spec.content?.node}
              </CardSpecificationContent>
            </CardSpecificationText>
          </CardSpecificationItem>
        ))}
      </CardSpecifications>
    );
  };

  const renderSubtitle = () => {
    const subtitleNode = subtitle?.node;
    if (type !== "seminovos" || !showSubtitle || !subtitleNode) return null;

    return <CardSubtitle data-af-field={afField(fieldPath, 'subtitle')}>{subtitleNode}</CardSubtitle>;
  };

  const renderButton = () => {
    const buttonNode = buttonText?.node;

    if (!showButton || !buttonNode) return null;
    
    const buttonContent = (
      <>
        {buttonNode}

        {buttonStyle === "link" && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 16 16"
          >
            <path
              fill="currentColor"
              fillRule="evenodd"
              d="M7.529 2.861c.26-.26.682-.26.942 0l4.667 4.667c.26.26.26.682 0 .943l-4.667 4.666a.667.667 0 1 1-.942-.942l3.528-3.529H3.333a.667.667 0 0 1 0-1.333h7.724L7.53 3.804a.667.667 0 0 1 0-.943"
              clipRule="evenodd"
            ></path>
          </svg>
        )}
      </>
    );

    // When the card is a link, use CardButtonVisual to avoid nested <a> tags
    if (buttonLink) {
      return (
        <CardButtonVisual
          data-af-field={afField(fieldPath, 'buttonText')}
          $buttonStyle={buttonStyle}
          $backgroundColor={buttonBgColor}
          $textColor={buttonTextColor}
          $linkColor={buttonLinkColor}
          $borderRadius={buttonBorderRadius}
          $borderColor={buttonBorderColor}
        >
          {buttonContent}
        </CardButtonVisual>
      );
    }

    return (
      <CardButton
        data-af-field={afField(fieldPath, 'buttonText')}
        href="#"
        $buttonStyle={buttonStyle}
        $backgroundColor={buttonBgColor}
        $textColor={buttonTextColor}
        $linkColor={buttonLinkColor}
        $borderRadius={buttonBorderRadius}
        $borderColor={buttonBorderColor}
        data-gtag-action="Clique Botão"
        data-gtag-category="Card Dinâmico"
        data-gtag-label={formatGtagLabel(htmlId, title.text, buttonText?.text, buttonLink)}
      >
        {buttonContent}
      </CardButton>
    );
  };

  return (
    <CardWrapper
      as={buttonLink ? "a" : "div"}
      href={buttonLink || undefined}
      target={buttonLink ? (openLinkInNewTab ? "_blank" : "_self") : undefined}
      rel={buttonLink && openLinkInNewTab ? "noopener noreferrer" : undefined}
      $type={type}
      $alignment={alignment}
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $borderRadius={borderRadius}
      data-testid="dynamic-card"
      data-gtag-action="Clique no card"
      data-gtag-category="Card Dinâmico"
      data-gtag-label={formatGtagLabel(htmlId, title.text, buttonText?.text, buttonLink)}
    >
      {renderImage()}
      <CardContent
        $alignment={alignment}
        $type={type}
      >
        <CardHeader $alignment={alignment}>
          {renderTag()}
          {renderTitle()}
        </CardHeader>

        <CardBody $alignment={alignment}>
          {renderDescription()}
          {type === "seminovos" &&
            showDescription &&
            description &&
            specifications &&
            specifications.length > 0 && <CardDivider />}

          {renderSpecifications()}

          {type === "seminovos" &&
            specifications &&
            specifications.length > 0 &&
            ((showSubtitle && subtitle) || (showButton && buttonText)) && (
              <CardDivider />
            )}
        </CardBody>

        <CardFooter $alignment={alignment}>
          {renderSubtitle()}
          {renderMetadata()}
          {renderButton()}
        </CardFooter>
      </CardContent>
    </CardWrapper>
  );
};
